import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { RuleNodeType } from 'constants/rules';

import { selectRuleGroupsList } from './ruleGroups.selectors';
import { selectUnderwriting } from './underwriting.selectors';

const selectRules = createSelector(selectUnderwriting, ({ rules }) => rules);

const selectRulesList = createSelector(selectRules, Object.values);

export const selectRulesListByGroupId = memoizeOne(groupId =>
  createSelector(selectRulesList, rules => rules.filter(rule => rule.groupId === groupId)),
);

export const selectRulesTree = createSelector(
  [selectRulesList, selectRuleGroupsList],
  (rules, ruleGroups) => {
    const preparedGroups = ruleGroups.map(ruleGroup => ({
      ...ruleGroup,
      nodeType: RuleNodeType.GROUP,
    }));
    const preparedRules = rules.map(rule => ({
      ...rule,
      parentId: rule.parentId || rule.groupId,
      nodeType: RuleNodeType.RULE,
    }));

    return [...preparedGroups, ...preparedRules];
  },
);

export const selectRuleById = memoizeOne(ruleId =>
  createSelector(selectRules, rules => {
    if (!rules[ruleId]) {
      return null;
    }

    const { userFriendlyExpression } = rules[ruleId];

    const collectInheritedExpressions = currentParentId => {
      if (!currentParentId) {
        return [];
      }

      const parentExpression = _.get(rules[currentParentId], 'userFriendlyExpression', '');
      const rest = collectInheritedExpressions(rules[currentParentId]?.parentId);

      return parentExpression ? [parentExpression, ...rest] : rest;
    };

    const inheritedExpressions = collectInheritedExpressions(rules[ruleId]?.parentId);

    return {
      ...rules[ruleId],
      inheritedExpressions: inheritedExpressions.length
        ? `AND(${inheritedExpressions.join(', ')})`
        : '',
      allExpressions: `AND(${[...inheritedExpressions, userFriendlyExpression].join(', ')})`,
    };
  }),
);
